import type { HTMLAttributes } from "react";
import { cn } from "@/lib/utils/cn";

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Opt-in premium hover treatment (lift + theme-tinted shadow + border
   * tint) for cards that are clickable or otherwise want to read as
   * "responsive" (KPI/analytics tiles) - generalises ModuleGrid's ModuleCard
   * hover into the shared primitive. Static/informational cards should
   * leave this unset - not every card should wobble on hover. */
  interactive?: boolean;
  /** Only relevant when `interactive` - which theme-tinted glow to use.
   * Defaults to "primary" (Royal Purple); pass "accent" for a card that
   * already carries the Pink accent tone elsewhere (e.g. an accent-toned
   * KPI card), so the hover glow matches the card's own color rather than
   * defaulting to purple everywhere. */
  glow?: "primary" | "accent";
}

export function Card({ className, interactive, glow = "primary", ...props }: CardProps) {
  return (
    <div
      className={cn(
        "rounded-xl2 border border-slate-200 bg-white shadow-soft",
        interactive &&
          cn(
            // Strengthened 2026-08-18 (v2): -translate-y-1 (~3px, up from
            // -0.5/~2px) reads as a clearer lift, and a real pressed step
            // (active:) returns most of the way to rest - default -> hover-
            // elevated -> pressed-reduced -> released, the tactile
            // hierarchy the interaction spec asks for, without looking like
            // neumorphism (still a flat surface, just one that visibly
            // responds).
            //
            // Strengthened again 2026-08-19 (v3): the border-color-only tint
            // read as too subtle to register as "this is a selectable area"
            // at a glance - added a ring (box-shadow, so it doesn't reflow
            // the layout the way thickening the border would) so the whole
            // card gets a genuine outline on hover, not just a faint color
            // shift.
            "transition-all duration-200 ease-out hover:-translate-y-1 hover:border-primary-300 hover:ring-2 hover:ring-primary-100 active:translate-y-0 active:shadow-soft active:duration-100",
            glow === "accent" ? "hover:shadow-hover-accent" : "hover:shadow-hover",
          ),
        className,
      )}
      {...props}
    />
  );
}

export function CardHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex items-center justify-between border-b border-slate-100 px-5 py-4", className)} {...props} />;
}

export function CardTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return <h3 className={cn("text-sm font-semibold text-slate-900", className)} {...props} />;
}

export function CardBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("p-5", className)} {...props} />;
}

export function CardFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-3", className)} {...props} />;
}
